import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Pulse } from "./Pulse";
import { ApiError } from "../../lib/api";

export interface ConfirmItem {
  label: string;
  meta?: string;
}

export interface ConfirmOptions {
  tier: 1 | 2;
  /** "Re-run 3 failed runs?" */
  title: string;
  /** What will be written, where — plain words. */
  body: ReactNode;
  /** Optional list of the things affected, e.g. the runs selected. */
  items?: ConfirmItem[];
  /** "Re-run 3 runs" / "Delete client" */
  confirmLabel: string;
  /** Required for tier 2. Compared case-insensitively, whitespace trimmed. */
  typedPhrase?: string;
  /** When given, the modal runs it and only resolves true once it succeeds. */
  onConfirm?: () => Promise<void>;
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(() =>
  Promise.reject(new Error("useConfirm() needs a <ConfirmProvider> above it.")),
);

/**
 * The app's only confirmation path (spec §5.1, §7): `await confirm({...})`
 * resolves true when the person went through with it.
 *
 * Tier 1 states the consequence in plain words behind a primary button; tier 2
 * demands a typed phrase and keeps the danger button disabled until it
 * matches. Pass `onConfirm` and the dialog runs the work itself, showing
 * "Working" and staying open on failure so the person can retry or cancel.
 *
 * `window.confirm`, `prompt` and `alert` are not used in the admin UI.
 */
export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext);
}

/** Whether a typed tier 2 phrase matches, case-insensitively and trimmed. */
export function phraseMatches(typed: string, phrase: string): boolean {
  return typed.trim().toLowerCase() === phrase.trim().toLowerCase();
}

interface Pending {
  id: number;
  options: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

/**
 * Holds the pending confirmation and renders its dialog above the app.
 *
 * At most one is live at a time and the latest request wins — up to the moment
 * the operator confirms. A `confirm()` arriving while another dialog is merely
 * open settles the outgoing one false and replaces it, rather than queueing
 * behind it. Once the outgoing dialog's `onConfirm` is running the work is no
 * longer cancellable, so the newcomer is refused instead: it resolves false
 * straight away and the working dialog is left alone. Unmounting settles the
 * live request the same way, so an `await confirm(...)` never hangs because the
 * page navigated away.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  // The resolver lives here, not only in `pending` state, so replacing an
  // outgoing request and settling on unmount can act immediately rather than
  // depending on a state update landing first.
  const pendingRef = useRef<Pending | null>(null);
  const nextId = useRef(0);
  // True from the moment the live dialog's `onConfirm` starts until it settles.
  // Replacing a request in that window would resolve its caller false while the
  // work carried on and quite possibly succeeded — the caller would be told the
  // opposite of what happened. Before the work starts there is nothing to
  // contradict, so latest-wins still applies.
  const workingRef = useRef(false);

  // Request-scoped: a dialog whose request has already been replaced (its
  // `onConfirm` was still running when a newer confirm() call took over)
  // must not be able to settle the request that superseded it. Only the
  // dialog currently referenced by `pendingRef` may settle.
  const settle = useCallback((id: number, ok: boolean) => {
    if (pendingRef.current?.id !== id) return;
    workingRef.current = false;
    pendingRef.current.resolve(ok);
    pendingRef.current = null;
    setPending(null);
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => {
    // The live confirmation has already been agreed to and its work is running.
    // Refuse rather than replace: "not confirmed" is the truthful answer for a
    // dialog the operator was never shown, and the work in flight keeps the
    // dialog that is reporting on it.
    if (workingRef.current) return Promise.resolve(false);
    // Latest request wins: a confirm() call that arrives while one is still
    // pending settles the outgoing request with false and replaces it —
    // callers never queue behind an earlier confirmation.
    pendingRef.current?.resolve(false);
    return new Promise<boolean>((resolve) => {
      const next: Pending = { id: ++nextId.current, options, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  useEffect(() => {
    // If the provider itself unmounts with a request in flight (e.g. the
    // page navigated away), settle it with false rather than leaving the
    // caller's `await confirm(...)` hanging forever.
    return () => {
      workingRef.current = false;
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    };
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <ConfirmDialog
          key={pending.id}
          options={pending.options}
          onResolve={(ok) => settle(pending.id, ok)}
          onWorkingChange={(w) => { workingRef.current = w; }}
        />
      ) : null}
    </ConfirmContext.Provider>
  );
}

/**
 * One confirmation, rendered. Exported for tests; the app reaches it through
 * `useConfirm`.
 *
 * While `onConfirm` runs, every exit is closed — Cancel is disabled and the
 * dialog is not dismissible, because Escape and a backdrop click would
 * otherwise unmount it while the request carries on and land the work after
 * the operator was shown what looked like a cancellation.
 */
export function ConfirmDialog({
  options,
  onResolve,
  onWorkingChange,
}: {
  options: ConfirmOptions;
  onResolve: (ok: boolean) => void;
  /** Told when `onConfirm` starts and stops, so the provider can refuse to
   *  replace a confirmation whose work is already running. */
  onWorkingChange?: (working: boolean) => void;
}) {
  const [typed, setTyped] = useState("");
  const [working, setWorking] = useState(false);
  const [failure, setFailure] = useState<{ message: string; code?: string } | null>(null);

  const phrase = options.tier === 2 ? (options.typedPhrase ?? "") : null;
  // A tier 2 confirmation with no typed phrase configured must never be
  // satisfiable — phraseMatches("", "") would otherwise read as a match and
  // enable the danger button with nothing typed.
  const phraseOk = options.tier === 2 ? Boolean(phrase && phraseMatches(typed, phrase)) : true;

  /** Carry out the confirmed action, keeping the dialog up while it runs.
   *  A failure is shown in place rather than closing over it, so the operator
   *  sees what happened to the thing they just agreed to. */
  async function run() {
    if (!phraseOk || working) return;
    if (!options.onConfirm) {
      onResolve(true);
      return;
    }
    setWorking(true);
    onWorkingChange?.(true);
    setFailure(null);
    try {
      await options.onConfirm();
      onResolve(true);
    } catch (err) {
      setFailure({
        message: err instanceof Error ? err.message : String(err),
        code: err instanceof ApiError ? err.code : undefined,
      });
    } finally {
      setWorking(false);
      onWorkingChange?.(false);
    }
  }

  /** Back out, unless the action is already in flight. */
  function cancel() {
    if (!working) onResolve(false);
  }

  return (
    <Modal
      title={options.title}
      onClose={cancel}
      dismissible={!working}
      actions={
        <>
          <Button variant="secondary" onClick={cancel} disabled={working}>
            Cancel
          </Button>
          <Button
            variant={options.tier === 2 ? "danger" : "primary"}
            onClick={() => void run()}
            disabled={!phraseOk || working}
          >
            {working ? (
              <>
                <Pulse inline size={16} /> Working
              </>
            ) : (
              options.confirmLabel
            )}
          </Button>
        </>
      }
    >
      <div className="modal__copy">{options.body}</div>
      {options.items && options.items.length > 0 ? (
        <ul className="modal__items">
          {options.items.map((it, i) => (
            <li key={`${it.label}-${i}`}>
              <span>{it.label}</span>
              {it.meta ? <span className="mono modal__item-meta">{it.meta}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {phrase ? (
        <label className="modal__phrase">
          <span className="uppercase-label">
            Type <span className="mono">{phrase}</span> to continue
          </span>
          <input
            className="field__input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            aria-label={`Type ${phrase} to confirm`}
          />
        </label>
      ) : null}
      {failure ? (
        <div className="modal__failure" role="alert">
          <p className="modal__failure-text">This did not go through. {failure.message}</p>
          {failure.code ? <code className="mono modal__failure-code">{failure.code}</code> : null}
        </div>
      ) : null}
    </Modal>
  );
}
